/**
 * GlassPill — glass-morphism pill button/badge.
 *
 * Encapsulates G recipe tokens so callers use
 * <GlassPill variant="light" /> instead of raw inline style spread.
 * Atom purity: imports only react + tokens.
 */
import { CSSProperties, ReactNode } from 'react';
import { G, P } from '../../styles/tokens';

export interface GlassPillProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'light' | 'dark' | 'tinted' | 'warmTag' | 'successTag' | 'errorTag';
  size?: number;
  style?: CSSProperties;
}

/** Variants with white text (dark-surface glass). */
const DARK_VARIANTS = new Set(['dark', 'tinted', 'successTag', 'errorTag']);

export function GlassPill({
  children,
  onClick,
  variant = 'light',
  size = 36,
  style,
}: GlassPillProps) {
  const recipe = G[variant] ?? G.light;
  const color = DARK_VARIANTS.has(variant) ? '#ffffff' : P.ink;
  const Tag = onClick ? 'button' : 'span';

  const base: CSSProperties = {
    height: size,
    minWidth: size,
    borderRadius: 9999,
    padding: '0 12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    cursor: onClick ? 'pointer' : 'default',
    color,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: -0.1,
    boxSizing: 'border-box',
    ...recipe,   // recipe contains border, backdropFilter, background, boxShadow
    ...style,
  };

  return (
    <Tag onClick={onClick} style={base}>
      {children}
    </Tag>
  );
}
